/**
 * Shared layout for the 5 MetaBridge product pages (DMP, MSSanté, INS, PSC, FHIR).
 * Palette stricte : navy #0F2B5B / white #ffffff / mint #4FC3F7 / light #F0F9FF.
 * Hero split 50/50 — left navy, right white with features grid.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import SEO, { ORG_SCHEMA, faqSchema } from "../../components/SEO";

export default function MetaBridgePage({
  title,
  metaDescription,
  eyebrow,
  h1,
  intro,
  icon,
  bullets = [],
  details = [],
  useCases = [],
  faq = [],
  testIdSlug,
  canonical,
}) {
  return (
    <div style={{ background: "#F8FAFC", minHeight: "100vh" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonical}
        schema={faq.length > 0 ? [ORG_SCHEMA, faqSchema(faq)] : [ORG_SCHEMA]}
      />

      {/* Hero — split 50/50 navy + white */}
      <section
        className="relative pt-16"
        data-testid={`metabridge-${testIdSlug}-hero`}
      >
        <div className="grid lg:grid-cols-2 min-h-[520px]">
          {/* LEFT — Navy */}
          <div
            className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#0F2B5B" }}
          >
            <div
              className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(79,195,247,0.12)",
                border: "1px solid rgba(79,195,247,0.30)",
                color: "#4FC3F7",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              <Sparkles size={13} />
              MetaBridge · {eyebrow}
            </div>

            <h1
              className="mt-6 font-display"
              style={{
                color: "#ffffff",
                fontSize: "clamp(30px, 4.2vw, 50px)",
                fontWeight: 600,
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
              }}
            >
              {h1}
            </h1>

            <p
              className="mt-6 max-w-md"
              style={{ color: "#93B4D4", fontSize: 16, lineHeight: 1.55 }}
            >
              {intro}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/demo"
                className="inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
                style={{
                  background: "#4FC3F7",
                  color: "#0F2B5B",
                  fontWeight: 500,
                  fontSize: 14,
                  borderRadius: 8,
                  padding: "12px 22px",
                  textDecoration: "none",
                  boxShadow: "0 12px 28px rgba(79,195,247,0.32)",
                }}
              >
                Demander une démonstration <ArrowRight size={15} />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2"
                style={{
                  background: "transparent",
                  color: "#B8D4E8",
                  border: "0.5px solid rgba(184,212,232,0.30)",
                  fontSize: 13.5,
                  borderRadius: 8,
                  padding: "11px 20px",
                  textDecoration: "none",
                }}
              >
                Voir les autres briques
              </Link>
            </div>
          </div>

          {/* RIGHT — White with feature highlights */}
          <div
            className="px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#ffffff", borderLeft: "0.5px solid #E8EDF5" }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span style={{ width: 3, height: 22, background: "#4FC3F7", borderRadius: 2 }} />
              <span style={{ fontSize: 11, color: "#4FC3F7", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>
                Points clés
              </span>
            </div>
            <div
              className="inline-grid place-items-center h-12 w-12 rounded-xl mb-5"
              style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", color: "#0F2B5B" }}
            >
              {icon}
            </div>
            <ul className="grid grid-cols-1 gap-2.5">
              {bullets.slice(0, 5).map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-3"
                  style={{
                    background: "#F0F9FF",
                    border: "0.5px solid #BAE6FD",
                    borderRadius: 10,
                  }}
                >
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#4FC3F7" }} />
                  <span style={{ color: "#0F2B5B", fontSize: 13.5, lineHeight: 1.5, fontWeight: 500 }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Bullets — light theme */}
      {bullets.length > 5 && (
        <section className="py-16 lg:py-20" data-testid={`metabridge-${testIdSlug}-bullets`} style={{ background: "#F8FAFC" }}>
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10 text-center" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
              Ce que vous gagnez
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-4"
                  style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}
                >
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0" style={{ color: "#4FC3F7" }} />
                  <span className="text-[14px] leading-relaxed" style={{ color: "#0F2B5B" }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Details — light */}
      {details.length > 0 && (
        <section className="py-16 lg:py-20" data-testid={`metabridge-${testIdSlug}-details`} style={{ background: "#F8FAFC" }}>
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10 text-center" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
              Au cœur de la brique
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {details.map((d, i) => (
                <div
                  key={i}
                  className="p-6"
                  style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}
                >
                  <h3 className="font-display text-[17px] leading-snug" style={{ color: "#0F2B5B", fontWeight: 500 }}>{d.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "#64748B" }}>{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use cases — light */}
      {useCases.length > 0 && (
        <section className="py-16 lg:py-20" data-testid={`metabridge-${testIdSlug}-usecases`} style={{ background: "#F8FAFC" }}>
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10 text-center" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
              Cas d&apos;usage
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {useCases.map((u, i) => (
                <div
                  key={i}
                  className="p-6"
                  style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12, boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#4FC3F7" }}>{u.tag}</div>
                  <h3 className="mt-2 font-display text-[17px] leading-snug" style={{ color: "#0F2B5B", fontWeight: 500 }}>{u.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "#64748B" }}>{u.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — light */}
      {faq.length > 0 && (
        <section className="py-16 lg:py-20" data-testid={`metabridge-${testIdSlug}-faq`} style={{ background: "#F8FAFC" }}>
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10 text-center" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
              Questions fréquentes
            </h2>
            <div className="grid gap-3">
              {faq.map((q, i) => (
                <details
                  key={i}
                  className="p-5 group"
                  style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", borderRadius: 12 }}
                >
                  <summary className="cursor-pointer font-display text-[15px] flex items-center justify-between gap-3" style={{ color: "#0F2B5B", fontWeight: 500 }}>
                    {q.q} <ArrowRight size={14} className="group-open:rotate-90 transition" style={{ color: "#4FC3F7" }} />
                  </summary>
                  <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: "#64748B" }}>{q.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA — light */}
      <section className="py-16 lg:py-20" data-testid={`metabridge-${testIdSlug}-cta`} style={{ background: "#F8FAFC" }}>
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <ShieldCheck size={28} className="mx-auto mb-4" style={{ color: "#4FC3F7" }} />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight" style={{ color: "#0F2B5B", fontWeight: 600, letterSpacing: "-0.015em" }}>
            Discutons de votre projet d&apos;intégration.
          </h2>
          <p className="mt-5 text-[15px] max-w-xl mx-auto" style={{ color: "#64748B" }}>
            Un expert MetaConnect vous accompagne sur le cadrage, l&apos;intégration et la mise en
            conformité — quelle que soit votre stack actuelle.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/demo"
              className="inline-flex items-center gap-2"
              style={{
                background: "#0F2B5B", color: "#ffffff",
                borderRadius: 999, padding: "12px 24px", fontSize: 14, fontWeight: 500,
                textDecoration: "none",
                boxShadow: "0 8px 22px -10px rgba(15,43,91,0.45)",
              }}
            >
              Demander une démonstration <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2"
              style={{
                background: "#F1F5F9", color: "#0F2B5B",
                borderRadius: 999, padding: "11px 22px", fontSize: 13.5, fontWeight: 500,
                textDecoration: "none",
              }}
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
